import { CheckboxSelect, CheckboxSelectOption } from '@widgets/index';
import { useEffect, useMemo, useState } from 'react';
import { ISort } from '@entities/util/ISort';
import CustomRangeDatePicker from '@widgets/range-date-picker-with-modes';
import { useManagerOptions } from '@app/queries/options/useManagerOptions';
import { useRegionOptions } from '@app/queries/options/useRegionOptions';
import { useSourceOptions } from '@app/queries/options/useSourceOptions';
import { useTagOptions } from '@app/queries/options/useTagOptions';
import { useActivityOptions } from '@app/queries/options/useActivityOptions';
import { useAccountCreatedAtOptions } from '@app/queries/options/useAccountCreatedAtOptions';
import { useSelector } from 'react-redux';
import { selectLastUploadedReportAt } from '@app/redux/features/userSlice';
import { getDateShortcuts, parseDDMMYYYY } from '@app/utils/date';
import { DEFAULT_PER_PAGE } from '@widgets/paginator';
import { useAccountTypeOptions } from '@app/queries/options/useAccountTypeOptions';
import { useAccountOptions } from '@app/queries/options/useAccountOptions';
import { X } from 'lucide-react';

export type Filter = {
  dateFrom: string;
  dateTo: string;
  created_at: Array<string>;
  activity: Array<string>;
  managers: Array<string>;
  regions: Array<string>;
  sources: Array<string>;
  accounts: Array<string>;
  tags: Array<string>;
  accountType: Array<string>;
  page: number;
  byType: boolean;
  search: string;
  sort: ISort | null;
  perPage: number;
};

export const AccountDynamicsDefaultFilter: Filter = {
  dateFrom: '',
  dateTo: '',
  created_at: [],
  activity: [],
  managers: [],
  regions: [],
  sources: [],
  tags: [],
  accounts: [],
  accountType: [],
  byType: true,
  page: 1,
  search: '',
  sort: null,
  perPage: DEFAULT_PER_PAGE,
};

type Props = {
  onChangeFilters: (values: Filter) => void;
  filters: Filter;
  onReset: VoidFunction;
};

const AccountDynamicsFilter = ({ onChangeFilters, filters, onReset }: Props) => {
  const managerOptionsFetcher = useManagerOptions();
  const regionOptionsFetcher = useRegionOptions();
  const sourceOptionsFetcher = useSourceOptions({ includeHidden: true });
  const tagOptionsFetcher = useTagOptions({ includeHidden: true });
  const activityFetcher = useActivityOptions();
  const accountCreatedAtFetcher = useAccountCreatedAtOptions();
  const accountTypeFetcher = useAccountTypeOptions();
  const accountOptionsFetcher = useAccountOptions();

  const [managerOptions, setManagerOptions] = useState<Array<CheckboxSelectOption>>();
  const [regionOptions, setRegionOptions] = useState<Array<CheckboxSelectOption>>();
  const [sourceOptions, setSourceOptions] = useState<Array<CheckboxSelectOption>>();
  const [tagOptions, setTagOptions] = useState<Array<CheckboxSelectOption>>();
  const [typeOptions, setTypeOptions] = useState<Array<CheckboxSelectOption>>();
  const [accountOptions, setAccountOptions] = useState<Array<CheckboxSelectOption>>();

  const reportLastSyncDate = useSelector(selectLastUploadedReportAt);

  const [activityOptions, setActivityOptions] = useState<Array<CheckboxSelectOption>>();
  const [accountCreatedAtOptions, setAccountCreatedAtOptions] =
    useState<Array<CheckboxSelectOption>>();

  useEffect(() => {
    (async () => {
      const data = await managerOptionsFetcher.fetchData();
      setManagerOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await regionOptionsFetcher.fetchData();
      setRegionOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await sourceOptionsFetcher.fetchData();
      setSourceOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await tagOptionsFetcher.fetchData();
      setTagOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await accountTypeFetcher.fetch();
      setTypeOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await activityFetcher.fetch();
      setActivityOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await accountCreatedAtFetcher.fetch();
      setAccountCreatedAtOptions(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const data = await accountOptionsFetcher.fetchData();
      setAccountOptions(data);
    })();
  }, []);

  const customDateShortcuts = useMemo(() => {
    if (!reportLastSyncDate) return;

    const parsedReportLastSyncDate = parseDDMMYYYY(reportLastSyncDate);
    if (Number.isNaN(parsedReportLastSyncDate.getTime())) return undefined;

    return {
      ...getDateShortcuts(parsedReportLastSyncDate),
      all: {
        text: 'Все',
        period: {
          start: new Date(new Date().setTime(1639699202000)),
          end: new Date(),
        },
      },
    };
  }, [reportLastSyncDate]);

  const hasActiveFilters = useMemo(() => {
    return !!(
      filters?.dateFrom ||
      filters?.dateTo ||
      filters?.created_at?.length ||
      filters?.activity?.length ||
      filters?.managers?.length ||
      filters?.regions?.length ||
      filters?.sources?.length ||
      filters?.accounts?.length ||
      filters?.tags?.length ||
      filters?.accountType?.length
    );
  }, [filters]);

  return (
    <div className="flex flex-wrap gap-[10px] items-center">
      <CustomRangeDatePicker
        onChange={values =>
          onChangeFilters({
            ...filters,
            dateFrom: values.startDate,
            dateTo: values.endDate,
          })
        }
        /** @ts-ignore */
        maxDate={
          reportLastSyncDate ? new Date(reportLastSyncDate.split('.').reverse().join('-')) : ''
        }
        width={180}
        shortcuts={customDateShortcuts}
        value={{ startDate: filters?.dateFrom, endDate: filters?.dateTo }}
      />
      <CheckboxSelect
        title="Активность"
        value={
          activityOptions?.length
            ? activityOptions?.filter(item => filters?.activity?.includes(item.value))
            : []
        }
        isLoading={!activityFetcher.isSuccess}
        options={activityOptions}
        onChange={values =>
          onChangeFilters({ ...filters, activity: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        title="Менеджер"
        value={
          managerOptions?.length
            ? managerOptions?.filter(item => filters.managers?.includes(item.value))
            : []
        }
        isLoading={!managerOptionsFetcher.isSuccess}
        options={managerOptions}
        onChange={values =>
          onChangeFilters({ ...filters, managers: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        title="Регион"
        value={
          regionOptions?.length
            ? regionOptions?.filter(item => filters.regions?.includes(item.value))
            : []
        }
        isLoading={!regionOptionsFetcher.isSuccess}
        options={regionOptions}
        onChange={values =>
          onChangeFilters({ ...filters, regions: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        value={
          sourceOptions?.length
            ? sourceOptions?.filter(item => filters.sources?.includes(item.value))
            : []
        }
        isLoading={!sourceOptionsFetcher.isSuccess}
        title="Источник"
        options={sourceOptions}
        onChange={values =>
          onChangeFilters({ ...filters, sources: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        isLoading={!tagOptionsFetcher.isSuccess}
        value={
          tagOptions?.length ? tagOptions?.filter(item => filters.tags?.includes(item.value)) : []
        }
        title="Тег"
        options={tagOptions}
        onChange={values => onChangeFilters({ ...filters, tags: values.map(item => item.value) })}
      />
      <CheckboxSelect
        isLoading={!accountCreatedAtFetcher.isSuccess}
        value={
          accountCreatedAtOptions?.length
            ? accountCreatedAtOptions?.filter(item => filters?.created_at?.includes(item.value))
            : []
        }
        title="Дата создания"
        options={accountCreatedAtOptions}
        onChange={values =>
          onChangeFilters({ ...filters, created_at: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        isLoading={!accountTypeFetcher.isSuccess}
        value={
          typeOptions?.length
            ? typeOptions?.filter(item => filters?.accountType?.includes(item.value))
            : []
        }
        title="Тип"
        options={typeOptions}
        onChange={values =>
          onChangeFilters({ ...filters, accountType: values.map(item => item.value) })
        }
      />
      <CheckboxSelect
        isLoading={!accountOptionsFetcher.isSuccess}
        value={
          accountOptions?.length
            ? accountOptions?.filter(item => filters?.accounts?.includes(item.value))
            : []
        }
        title="Аккаунт"
        options={accountOptions}
        onChange={values =>
          onChangeFilters({ ...filters, accounts: values.map(item => item.value) })
        }
      />
      {hasActiveFilters && (
        <button type="button" onClick={onReset}>
          <X size={16} className="text-gray-300 cursor-pointer" />
        </button>
      )}
    </div>
  );
};

export default AccountDynamicsFilter;
